import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toastServerError } from "helpers";

import {
  ESupportTicketSource,
  SupportTicketResponse,
} from "../../aiTickets.types";
import { SUPPORT_TICKETS_QUERY_KEY } from "../../constants/enums";
import {
  requestSendTicketMessage,
  requestSetTicketConversationMode,
} from "./ticketConversationActions.request";

const conversationIdOf = (ticket: SupportTicketResponse): string | null =>
  ticket.source === ESupportTicketSource.WhatsApp
    ? ticket.whatsappConversationId
    : ticket.inAppConversationId;

export const useTicketConversationActions = (
  ticket: SupportTicketResponse | null,
) => {
  const queryClient = useQueryClient();
  const conversationId = ticket ? conversationIdOf(ticket) : null;

  const invalidateTranscript = () => {
    queryClient.invalidateQueries({
      queryKey: [
        SUPPORT_TICKETS_QUERY_KEY,
        "transcript",
        ticket?.source,
        conversationId,
      ],
    });
  };

  const invalidateConversation = () => {
    queryClient.invalidateQueries({
      queryKey: [
        SUPPORT_TICKETS_QUERY_KEY,
        "conversation",
        ticket?.source,
        conversationId,
      ],
    });
  };

  const sendMessage = useMutation({
    mutationFn: async (payload: {
      text?: string;
      imageUrl?: string;
      audioUrl?: string;
    }) => {
      if (!ticket || !conversationId) throw new Error("no conversation");
      return await requestSendTicketMessage({
        source: ticket.source,
        conversationId,
        ...payload,
      });
    },
    onError: toastServerError,
    onSuccess: () => invalidateTranscript(),
  });

  const setMode = useMutation({
    mutationFn: async (mode: "ai" | "human") => {
      if (!ticket || !conversationId) throw new Error("no conversation");
      return await requestSetTicketConversationMode({
        source: ticket.source,
        conversationId,
        mode,
      });
    },
    onError: toastServerError,
    onSuccess: () => invalidateConversation(),
  });

  return { sendMessage, setMode };
};
